import React, { Suspense, lazy } from "react";
import "regenerator-runtime/runtime";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { APIContextProvider } from "./utils/api";
import { UserInfoContextProvider } from "./utils/user";
import { WorkoutImportJobsProvider } from "./contexts/WorkoutImportJobsContext";

import { AuthContextProvider } from "./utils/auth";
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";
import Home from "./components/Home";
import RouteSeo from "./components/RouteSeo";

// Route-level code splitting: only the layout shell and the landing page are
// in the initial bundle. Every other route (and the Stripe-loading trial gate)
// is fetched on demand, keeping the marketing pages fast on mobile.
const About = lazy(() => import("./components/About"));
const HowItWorks = lazy(() => import("./components/HowItWorks"));
const Pricing = lazy(() => import("./components/Pricing"));
const ClientIntakeForm = lazy(() => import("./components/ClientIntakeForm"));
const Payment = lazy(() => import("./components/Payment/Payment"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const Subscription = lazy(() =>
  import("./components/ClientPortal/Subscription")
);
const ClientIntakes = lazy(() =>
  import("./components/CoachPortal/ClientIntakes")
);
const LoginCallback = lazy(() => import("./components/LoginCallback"));
const MyWorkouts = lazy(() => import("./components/ClientPortal/MyWorkouts"));
const Coaching = lazy(() => import("./components/CoachSignUp/Coaching"));
const LogWorkout = lazy(() => import("./components/ClientPortal/LogWorkout"));
const Login = lazy(() => import("./components/Login/Login"));
const Clients = lazy(() => import("./components/CoachPortal/Clients"));
const SetPassword = lazy(() => import("./components/Login/SetPassword"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const AppDownloadBanner = lazy(() => import("./components/AppDownloadBanner"));
const Support = lazy(() => import("./components/Support"));
const ReviewClients = lazy(() =>
  import("./components/CoachPortal/ReviewClients")
);
const ContentLibrary = lazy(() =>
  import("./components/ContentLibrary/ContentLibrary")
);
const Messaging = lazy(() => import("./components/CoachPortal/Messaging"));
const ManageProducts = lazy(() =>
  import("./components/CoachPortal/ManageProducts")
);
const CoachProfileAdmin = lazy(() =>
  import("./components/CoachPortal/CoachProfileAdmin")
);
const PublicCoachProfile = lazy(() =>
  import("./components/CoachPortal/PublicCoachProfile")
);
const InstagramCallback = lazy(() =>
  import("./components/CoachPortal/InstagramCallback")
);
const CoachingConnectedAccountReturn = lazy(() =>
  import("./components/CoachSignUp/CoachingConnectedAccountReturn")
);
const CoachingConnectedAccountRefresh = lazy(() =>
  import("./components/CoachSignUp/CoachingConnectedAccountRefresh")
);
const CoachingSignUpCreateStripeAccount = lazy(() =>
  import("./components/CoachSignUp/CoachingSignUpCreateStripeAccount")
);
const CoachSignUpLanding = lazy(() =>
  import("./components/CoachSignUp/CoachSignUpLanding")
);
const CoachingSignUpCreateAccountLink = lazy(() =>
  import("./components/CoachSignUp/CoachingSignUpCreateAccountLink")
);
const CoachingSignUpComplete = lazy(() =>
  import("./components/CoachSignUp/CoachingSignUpComplete")
);
const WorkoutImport = lazy(() =>
  import("./components/CoachPortal/WorkoutImport")
);
const ProgramCreator = lazy(() =>
  import("./components/CoachPortal/ProgramCreator")
);
const Templates = lazy(() => import("./components/CoachPortal/Templates"));
const Payouts = lazy(() => import("./components/CoachPortal/Payouts"));
const CoachSubscription = lazy(() =>
  import("./components/CoachPortal/CoachSubscription")
);
const Admin = lazy(() => import("./components/Admin/Admin"));
const CoachTrialExpiredGate = lazy(() =>
  import("./components/CoachPortal/CoachTrialExpiredGate")
);

const emotionCache = createCache({
  key: "mui",
  prepend: true,
});

const RouteFallback = () => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress color="primary" />
  </Box>
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#ff0000",
    },
    secondary: {
      main: "#ff0000",
    },
    info: {
      main: "#fff",
    },
  },
  typography: {
    fontFamily: ["Roboto Mono", "monospace"].join(","),
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: "#ffcccc",
          color: "#b30000",
          "& .MuiAlert-icon": {
            color: "#b30000",
          },
        },
        standardWarning: {
          backgroundColor: "#ff0000",
          color: "#fff",
          "& .MuiAlert-icon": {
            color: "#fff",
          },
        },
      },
    },
  },
});

const App = () => {
  const pullForHopesSignupElement = (
    <CoachSignUpLanding
      headline="Pull for Hopes Coach Signup"
      subheadline="Coaches who sign up at Pull for Hopes on May 9, 2026 get six months free."
      promoCode="pull-for-hopes-2026"
      promoEventName="Special event offer"
    />
  );

  return (
    <Router>
      <RouteSeo />
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthContextProvider>
          <APIContextProvider>
            <UserInfoContextProvider>
              <WorkoutImportJobsProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box
                  sx={{
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Header />
                  <Suspense fallback={null}>
                    <CoachTrialExpiredGate />
                  </Suspense>

                  <Box
                    component="main"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      minHeight: 0,
                    }}
                  >
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" exact element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/about-us" element={<About />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/coaching" element={<Coaching />} />
                  <Route path="/free-trial" element={<CoachSignUpLanding />} />
                  {/* Legacy URL: redirect to /free-trial */}
                  <Route
                    path="/coaching/landing"
                    element={<Navigate to="/free-trial" replace />}
                  />
                  <Route
                    path="/pull-for-hope"
                    element={pullForHopesSignupElement}
                  />
                  <Route
                    path="/coaching/pull-for-hope"
                    element={pullForHopesSignupElement}
                  />
                  <Route
                    path="/coaching/create-account"
                    element={<CoachingSignUpCreateStripeAccount />}
                  />
                  <Route
                    path="/coaching/create-account-link"
                    element={<CoachingSignUpCreateAccountLink />}
                  />
                  <Route
                    path="/coaching/complete"
                    element={<CoachingSignUpComplete />}
                  />
                  <Route
                    path="/coaching/return/:accountId"
                    element={<CoachingConnectedAccountReturn />}
                  />
                  <Route
                    path="/coaching/refresh/:accountId"
                    element={<CoachingConnectedAccountRefresh />}
                  />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/support" element={<Support />} />
                  <Route
                    path="/client-intake/:encodedCoachId"
                    element={<ClientIntakeForm />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/set-password" element={<SetPassword />} />
                  <Route path="/workouts" element={<ProtectedRoute />}>
                    <Route path="/workouts" element={<MyWorkouts />} />
                    <Route
                      path="/workouts/:workoutId"
                      element={<LogWorkout />}
                    />
                  </Route>
                  <Route path="/clients" element={<ProtectedRoute />}>
                    <Route path="/clients" element={<Clients />} />
                  </Route>
                  <Route path="/client-intakes" element={<ProtectedRoute />}>
                    <Route path="/client-intakes" element={<ClientIntakes />} />
                  </Route>
                  <Route path="/client-activity" element={<ProtectedRoute />}>
                    <Route
                      path="/client-activity"
                      element={<ReviewClients />}
                    />
                  </Route>
                  <Route path="/templates" element={<ProtectedRoute />}>
                    <Route path="/templates" element={<Templates />} />
                  </Route>
                  <Route path="/messaging" element={<ProtectedRoute />}>
                    <Route path="/messaging" element={<Messaging />} />
                  </Route>
                  <Route path="/manage-products" element={<ProtectedRoute />}>
                    <Route
                      path="/manage-products"
                      element={<ManageProducts />}
                    />
                  </Route>
                  <Route path="/coach-profile" element={<ProtectedRoute />}>
                    <Route
                      path="/coach-profile"
                      element={<CoachProfileAdmin />}
                    />
                  </Route>
                  <Route
                    path="/coaches/:slug"
                    element={<PublicCoachProfile />}
                  />
                  <Route path="/instagram/callback" element={<InstagramCallback />} />
                  <Route path="/payment" element={<ProtectedRoute />}>
                    <Route path="/payment" element={<Payment />} />
                  </Route>
                  <Route path="/subscription" element={<ProtectedRoute />}>
                    <Route path="/subscription" element={<Subscription />} />
                  </Route>
                  <Route path="/coach-subscription" element={<ProtectedRoute />}>
                    <Route
                      path="/coach-subscription"
                      element={<CoachSubscription />}
                    />
                  </Route>
                  <Route path="/callback" element={<LoginCallback />} />
                  <Route path="/content-library" element={<ProtectedRoute />}>
                    <Route
                      path="/content-library"
                      element={<ContentLibrary />}
                    />
                  </Route>
                  <Route path="/workout-import" element={<ProtectedRoute />}>
                    <Route path="/workout-import" element={<WorkoutImport />} />
                  </Route>
                  <Route path="/program-creator" element={<ProtectedRoute />}>
                    <Route
                      path="/program-creator"
                      element={<ProgramCreator />}
                    />
                  </Route>
                  <Route path="/payouts" element={<ProtectedRoute />}>
                    <Route path="/payouts" element={<Payouts />} />
                  </Route>
                  <Route path="/admin" element={<ProtectedRoute />}>
                    <Route path="/admin" element={<Admin />} />
                  </Route>
                  <Route
                    path="/impersonate"
                    element={<Navigate to="/admin" replace />}
                  />
                  <Route path="*" element={<Navigate to="" />} />
                </Routes>
                </Suspense>
                  </Box>

                <Footer />
                <Suspense fallback={null}>
                  <AppDownloadBanner />
                </Suspense>
                </Box>
              </LocalizationProvider>
              </WorkoutImportJobsProvider>
            </UserInfoContextProvider>
          </APIContextProvider>
          </AuthContextProvider>
        </ThemeProvider>
      </CacheProvider>
    </Router>
  );
};

export default App;
